/**
 * V2 Frontend Auth Utils
 * 
 * This utility reads authentication tokens from the same cookie format
 * used by v2-frontend (new_demo_frontend), allowing front_compliance
 * to share the same authentication with v2-frontend when using the same backend.
 */

const MAX_COOKIE_SIZE = 4000; // Browser cookie limit is ~4096 bytes
const SAFE_COOKIE_SIZE = 3800; // Account for cookie name, attributes, and domain overhead (~200-300 bytes for safety)
const COOKIE_PREFIX = 'd_c_c_t_p_'; // Cookie prefix used by v2-frontend

/**
 * Helper to get a cookie value by name.
 */
function getCookie(name: string): string | null {
  if (typeof window === "undefined") return null;
  
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

/**
 * Retrieves and reconstructs the authentication token from multiple cookies.
 * Uses the same format as v2-frontend: d_c_c_t_p_1, d_c_c_t_p_2, etc.
 * Returns null if missing or invalid.
 */
export function getV2Token(): string | null {
  if (typeof window === "undefined") return null;

  let tokenParts: string[] = [];
  let partIndex = 1;

  // Collect all token parts (d_c_c_t_p_1, d_c_c_t_p_2, ...)
  while (true) {
    const tokenPart = getCookie(`${COOKIE_PREFIX}${partIndex}`);
    if (!tokenPart) break;
    tokenParts.push(tokenPart);
    partIndex++;
  }

  if (tokenParts.length === 0) {
    return null;
  }

  const token = decodeURIComponent(tokenParts.join(""));
  return token;
}

/**
 * Clears all authentication token parts from cookies.
 * Uses the same format as v2-frontend.
 */
export function clearV2Token(domain?: string) {
  if (typeof window === "undefined") return;
  
  let partIndex = 1;
  const domainPart = domain ? `; domain=${domain}` : '';

  while (getCookie(`${COOKIE_PREFIX}${partIndex}`)) {
    document.cookie = `${COOKIE_PREFIX}${partIndex}=; path=/; secure; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT;${domainPart}`;
    partIndex++;
  }
}

/**
 * Checks if v2-frontend token exists in cookies
 */
export function hasV2Token(): boolean {
  return !!getV2Token();
}

/**
 * Get the domain for cookies (works for localhost)
 * In production, automatically detects the base domain if REACT_APP_DOMAIN is not set.
 * For localhost, returns empty string (cookies work without domain attribute).
 */
export function getCookieDomain(): string {
  if (typeof window === "undefined") return '';
  
  // First, check if domain is explicitly configured in environment
  const configuredDomain = process.env.REACT_APP_DOMAIN;
  
  if (configuredDomain && configuredDomain !== 'localhost' && configuredDomain !== '127.0.0.1') {
    return configuredDomain;
  }
  
  // If not configured or is localhost, check current hostname
  const hostname = window.location.hostname;
  
  // For localhost, don't set domain (cookies work without it)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  
  // For production domains, extract base domain (e.g., '.pianat.ai' from 'comply-now.pianat.ai')
  if (hostname.includes('.')) {
    const parts = hostname.split('.');
    if (parts.length > 2) {
      // Return base domain with leading dot for subdomain sharing
      return '.' + parts.slice(-2).join('.');
    }
    // If only 2 parts (e.g., 'example.com'), return as is
    return hostname;
  }
  
  // Default: no domain (works for localhost and exact hostname matches)
  return '';
}

/**
 * Helper to split a string into chunks
 */
function splitString(str: string, chunkSize: number): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.substring(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Clears ALL cookies with the v2 token prefix, regardless of domain or path.
 * This is more aggressive and ensures we catch all variations.
 */
function clearAllV2TokenCookies() {
  if (typeof window === "undefined") return;
  
  const domain = getCookieDomain();
  const paths = ['/', '']; // Try both with and without path
  const domains = domain ? [domain, ''] : ['']; // Try both with and without domain
  
  // Clear cookies for all possible combinations of domain and path
  // Also check a wide range of indices to catch orphaned cookies
  for (let i = 1; i <= 100; i++) {
    const cookieName = `${COOKIE_PREFIX}${i}`;
    
    // Try clearing with different domain/path combinations
    for (const dom of domains) {
      for (const path of paths) {
        const domainPart = dom ? `; domain=${dom}` : '';
        const pathPart = path ? `; path=${path}` : '; path=/';
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;${pathPart}; secure; samesite=strict${domainPart}`;
      }
    }
  }
}

/**
 * Clears ALL cookies from the browser (comprehensive logout cleanup).
 * This function aggressively clears all cookies including v2 token cookies,
 * front_compliance cookies, and any other cookies that might exist.
 * Similar to how new_demo_frontend handles logout.
 */
export function clearAllCookies() {
  if (typeof window === "undefined") return;
  
  try {
    // First, clear all v2 token cookies aggressively
    clearAllV2TokenCookies();
    
    // Clear v2 token using the standard function (with domain)
    const domain = getCookieDomain();
    clearV2Token(domain);
    
    // Get all cookies and clear them individually
    const cookies = document.cookie.split(";");
    const cookieDomain = getCookieDomain();
    const domainsToTry = cookieDomain ? [cookieDomain, ''] : [''];
    const pathsToTry = ['/', ''];
    
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      if (name) {
        // Try clearing with different domain/path combinations
        for (const dom of domainsToTry) {
          for (const path of pathsToTry) {
            const domainPart = dom ? `; domain=${dom}` : '';
            const pathPart = path ? `; path=${path}` : '; path=/';
            // Try with secure and samesite
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;${pathPart}; secure; samesite=strict${domainPart}`;
            // Try without secure/samesite (for cookies that don't have them)
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;${pathPart}${domainPart}`;
          }
        }
      }
    });
    
    // Also clear common cookie names explicitly
    const commonCookies = [
      'ntra_assessment_compliance_token',
      'ntra_assessment_compliance_refresh_token',
      'user',
      'ntra_compliance_csrf_token',
      'i18next',
      'shared_app_language',
      'selectedLanguage'
    ];
    
    for (const cookieName of commonCookies) {
      for (const dom of domainsToTry) {
        for (const path of pathsToTry) {
          const domainPart = dom ? `; domain=${dom}` : '';
          const pathPart = path ? `; path=${path}` : '; path=/';
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;${pathPart}; secure; samesite=strict${domainPart}`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;${pathPart}${domainPart}`;
        }
      }
    }
  } catch (error) {
    console.warn('Error clearing all cookies:', error);
  }
}

/**
 * Optimizes cookies before navigation by clearing old token parts and re-setting only the current token.
 * This prevents cookie accumulation that can cause "Request Header Or Cookie Too Large" errors.
 */
export function optimizeV2CookiesBeforeNavigation() {
  if (typeof window === "undefined") return;

  try {
    // Get the current token before clearing (without triggering redirect)
    let tokenParts: string[] = [];
    let partIndex = 1;

    // Collect all token parts without calling getV2Token() to avoid any side effects
    while (true) {
      const tokenPart = getCookie(`${COOKIE_PREFIX}${partIndex}`);
      if (!tokenPart) break;
      tokenParts.push(tokenPart);
      partIndex++;
    }

    if (tokenParts.length > 0) {
      const currentToken = decodeURIComponent(tokenParts.join(""));
      const domain = getCookieDomain();
      const domainPart = domain ? `; domain=${domain}` : '';
      
      // Aggressively clear ALL existing token cookies (including any orphaned ones)
      // This clears cookies with different domains, paths, and indices
      clearAllV2TokenCookies();
      
      // Small delay to ensure cookies are cleared before re-setting
      // Re-set only the current token synchronously (this ensures we have a clean, minimal set)
      // This is done synchronously to ensure cookies are set before navigation
      const encodedToken = encodeURIComponent(currentToken);
      // Use SAFE_COOKIE_SIZE to account for cookie name, attributes, and domain overhead
      const newTokenParts = splitString(encodedToken, SAFE_COOKIE_SIZE);
      
      newTokenParts.forEach((part, index) => {
        const cookieName = `${COOKIE_PREFIX}${index + 1}`;
        document.cookie = `${cookieName}=${part}; path=/; secure; samesite=strict${domainPart}`;
      });
    } else {
      // Even if no token found, clear any orphaned cookies
      clearAllV2TokenCookies();
    }
  } catch (error) {
    console.warn('Error optimizing cookies before navigation:', error);
    // If optimization fails, at least try to clear orphaned cookies aggressively
    try {
      clearAllV2TokenCookies();
    } catch (clearError) {
      console.warn('Error clearing cookies:', clearError);
    }
  }
}

